import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash2 } from 'lucide-react';

// ★ 2026-08-10 — assign a stable UID to each installment row so React can
// track rows correctly across add/remove operations (previous code used the
// array index as key which caused state bleed between rows on removal).
let _installmentUidCounter = 0;
const _mkUid = () => {
  _installmentUidCounter += 1;
  return `inst-${Date.now().toString(36)}-${_installmentUidCounter}`;
};
const _ensureUid = (it) => (it && it._uid ? it : { ...(it || {}), _uid: _mkUid() });

/**
 * PaymentPlanEditor — Editor del piano pagamenti per un lead.
 *
 * Props:
 *   value: {
 *     payment_type: 'secco' | 'rate',
 *     currency: 'EUR',
 *     installments: [{amount, due_date, notes}, ...],
 *   }
 *   onChange(value): updater
 *   readOnly?: boolean
 *
 * Pagamento "secco" → forziamo 1 sola rata.
 * Pagamento "rate" → N rate libere, importi liberi.
 *
 * Importo totale = somma rate (calcolato live).
 */
export default function PaymentPlanEditor({ value, onChange, readOnly = false }) {
  const v = value || {
    payment_type: 'secco',
    currency: 'EUR',
    installments: [{ _uid: _mkUid(), amount: '', due_date: '', notes: '' }],
  };

  // Ensure every installment has a stable _uid (retro-fit for existing values
  // coming from the backend/DB which don't include _uid).
  React.useEffect(() => {
    if (!Array.isArray(v.installments)) return;
    const missing = v.installments.some((it) => !it || !it._uid);
    if (missing && typeof onChange === 'function' && !readOnly) {
      onChange({ ...v, installments: v.installments.map(_ensureUid) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setType = (type) => {
    if (readOnly) return;
    if (type === 'secco') {
      // Tronchiamo a 1 sola rata, mantenendo eventualmente la 1a
      const first = _ensureUid((v.installments && v.installments[0]) || { amount: '', due_date: '', notes: '' });
      onChange({ ...v, payment_type: 'secco', installments: [first] });
    } else {
      onChange({
        ...v,
        payment_type: 'rate',
        installments: v.installments && v.installments.length > 0
          ? v.installments.map(_ensureUid)
          : [{ _uid: _mkUid(), amount: '', due_date: '', notes: '' }],
      });
    }
  };

  const updateInstallment = (idx, patch) => {
    if (readOnly) return;
    const next = (v.installments || []).map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange({ ...v, installments: next });
  };

  const addInstallment = () => {
    if (readOnly) return;
    onChange({
      ...v,
      installments: [...(v.installments || []), { _uid: _mkUid(), amount: '', due_date: '', notes: '' }],
    });
  };

  const removeInstallment = (idx) => {
    if (readOnly) return;
    const list = v.installments || [];
    if (list.length <= 1) return; // sempre almeno 1 rata
    onChange({ ...v, installments: list.filter((_, i) => i !== idx) });
  };

  const total = (v.installments || []).reduce((acc, it) => acc + (parseFloat(it.amount) || 0), 0);

  return (
    <div className="space-y-4" data-testid="payment-plan-editor">
      {/* Type selector */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={readOnly}
          onClick={() => setType('secco')}
          data-testid="payment-type-secco"
          className={`px-4 py-3 rounded-lg border text-sm transition-colors ${
            v.payment_type === 'secco'
              ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]'
              : 'bg-background border-border hover:border-[hsl(var(--primary))]/30'
          } ${readOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <div className="font-medium">💰 Pagamento secco</div>
          <div className="text-xs opacity-80 mt-0.5">Un&apos;unica rata, importo totale + data</div>
        </button>
        <button
          type="button"
          disabled={readOnly}
          onClick={() => setType('rate')}
          data-testid="payment-type-rate"
          className={`px-4 py-3 rounded-lg border text-sm transition-colors ${
            v.payment_type === 'rate'
              ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]'
              : 'bg-background border-border hover:border-[hsl(var(--primary))]/30'
          } ${readOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <div className="font-medium">📅 Rate</div>
          <div className="text-xs opacity-80 mt-0.5">N rate libere, importi e date personalizzate</div>
        </button>
      </div>

      {/* Installments table */}
      <div className="space-y-2">
        {(v.installments || []).map((it, idx) => (
          <div
            key={it._uid || `inst-fallback-${idx}`}
            className="grid grid-cols-12 gap-2 items-end border rounded-lg p-3 bg-[var(--surface-2)]"
            data-testid={`installment-row-${idx}`}
          >
            <div className="col-span-1 text-xs text-muted-foreground pb-2">#{idx + 1}</div>
            <div className="col-span-3">
              <Label className="text-xs uppercase tracking-wide">Importo €</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={it.amount}
                onChange={(e) => updateInstallment(idx, { amount: e.target.value })}
                placeholder="0,00"
                disabled={readOnly}
                data-testid={`installment-amount-${idx}`}
                className="mt-1"
              />
            </div>
            <div className="col-span-3">
              <Label className="text-xs uppercase tracking-wide">Data scadenza</Label>
              <Input
                type="date"
                value={(it.due_date || '').slice(0, 10)}
                onChange={(e) => updateInstallment(idx, { due_date: e.target.value })}
                disabled={readOnly}
                data-testid={`installment-due-date-${idx}`}
                className="mt-1"
              />
            </div>
            <div className="col-span-4">
              <Label className="text-xs uppercase tracking-wide">Note</Label>
              <Input
                value={it.notes || ''}
                onChange={(e) => updateInstallment(idx, { notes: e.target.value })}
                placeholder="es. Anticipo, saldo…"
                disabled={readOnly}
                data-testid={`installment-notes-${idx}`}
                className="mt-1"
              />
            </div>
            <div className="col-span-1 flex justify-end pb-1">
              {v.payment_type === 'rate' && (v.installments || []).length > 1 && !readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeInstallment(idx)}
                  data-testid={`installment-remove-${idx}`}
                  type="button"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              )}
            </div>
          </div>
        ))}

        {v.payment_type === 'rate' && !readOnly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addInstallment}
            data-testid="installment-add"
            className="mt-1"
          >
            <Plus className="w-4 h-4 mr-1" /> Aggiungi rata
          </Button>
        )}
      </div>

      {/* Total */}
      <div className="flex items-center justify-between border-t pt-3">
        <div className="text-sm text-muted-foreground">
          {v.payment_type === 'rate'
            ? `${(v.installments || []).length} rate`
            : 'Pagamento secco'}
        </div>
        <div className="text-lg font-medium" data-testid="payment-plan-total">
          Totale:{' '}
          <span className="font-semibold tabular-nums">
            € {total.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>
    </div>
  );
}
